import {
  Box,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import { Link as RouterLink } from 'react-router-dom';
import { formatMatchClock } from '@/types';
import {
  formatLiveMatchDate,
  liveMatchHeadline,
  liveStatusColor,
} from '@/utils/liveSessionDisplay';
import { LIVE_PERIOD_LABELS, LIVE_STATUS_LABELS } from '@/utils/liveClock';
import type { LiveSession } from '@/types';

interface Props {
  session: LiveSession;
  onDelete?: (e: React.MouseEvent, id: string) => void;
}

export default function LiveSessionCard({ session, onDelete }: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headline = liveMatchHeadline(session);
  const dateLabel = formatLiveMatchDate(session.match_date);
  const statusLabel = LIVE_STATUS_LABELS[session.status] || session.status;
  const periodLabel =
    LIVE_PERIOD_LABELS[session.current_period as keyof typeof LIVE_PERIOD_LABELS] ||
    session.current_period;
  const clockSeconds = session.current_match_second ?? session.accumulated_seconds ?? 0;
  const isActive = session.status === 'live' || session.status === 'paused' || session.status === 'halftime';

  return (
    <Card
      sx={{
        mb: 1.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isActive ? alpha(theme.palette.secondary.main, 0.45) : 'divider',
        background: isDark
          ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.dark, 0.22)} 0%, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 55%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.35)} 0%, #fff 50%)`,
        boxShadow: isActive ? `0 8px 28px ${alpha(theme.palette.secondary.main, 0.18)}` : 1,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: isActive ? `0 12px 32px ${alpha(theme.palette.secondary.main, 0.22)}` : 3,
        },
      }}
    >
      <CardActionArea component={RouterLink} to={`/live/${session.id}`} sx={{ alignItems: 'stretch' }}>
        <Stack direction="row" spacing={0} sx={{ minHeight: 112 }}>
          <Box
            sx={{
              width: { xs: 108, sm: 124 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
              py: 1.5,
              bgcolor: isActive
                ? alpha(theme.palette.secondary.main, isDark ? 0.28 : 0.14)
                : alpha(theme.palette.action.hover, 0.6),
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            <SportsHandballIcon color="secondary" sx={{ fontSize: 22, mb: 0.5, opacity: 0.85 }} />
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 800,
                letterSpacing: 0.5,
                lineHeight: 1,
                color: isActive ? 'secondary.dark' : 'text.primary',
              }}
            >
              {formatMatchClock(clockSeconds)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>
              {periodLabel}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, p: 1.5, pr: onDelete ? 0.5 : 1.5 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
              {session.competition_name && (
                <Chip
                  size="small"
                  label={session.competition_name}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.secondary.main, 0.12),
                    color: 'secondary.dark',
                  }}
                />
              )}
              <Chip
                size="small"
                label={statusLabel}
                color={liveStatusColor(session.status)}
                variant={session.status === 'ended' ? 'outlined' : 'filled'}
                sx={{ height: 22, fontWeight: 700 }}
              />
            </Stack>

            <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ mb: 0.25 }}>
              {headline}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {dateLabel && (
                <Typography variant="body2" color="text.secondary">
                  {dateLabel}
                </Typography>
              )}
              {session.match_duration_minutes && (
                <Typography variant="body2" color="text.secondary">
                  {dateLabel ? '· ' : ''}
                  {session.match_duration_minutes} 分鐘
                </Typography>
              )}
            </Stack>
          </Box>

          {onDelete && session.can_edit && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: 0.75, pr: 0.5 }}>
              <IconButton
                size="small"
                color="error"
                aria-label="刪除"
                onClick={(e) => onDelete(e, session.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Stack>
      </CardActionArea>
    </Card>
  );
}
